// Mobile-first Re-sign Players screen — replaces ZenGM's desktop NegotiationList (the DataTable
// of expiring contracts you hit at the AFTER_DRAFT → RESIGN_PLAYERS phase). Same worker data +
// actions (acceptContractNegotiation / cancelContractNegotiation / reSignAll); the design's dark
// card language over the real backend, matching GmFreeAgency.

import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { useLocal } from "../util/local.ts";
import { toWorker } from "../util/toWorker.ts";
import { logEvent } from "../util/logEvent.ts";
import { confirm } from "../util/confirm.tsx";
import type { View } from "../../common/types.ts";

const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF",
	SG: "#2a7de1",
	SF: "#18E0A8",
	PF: "#E8B923",
	C: "#E2504E",
};

const STAT_COLS: [string, string][] = [
	["PPG", "pts"],
	["RPG", "trb"],
	["APG", "ast"],
];

const GmNegotiationList = ({
	capSpace,
	numRosterSpots,
	payroll,
	players,
	sumContracts,
}: View<"negotiationList">) => {
	useTitleBar({ title: "Re-sign Players" });

	const { teamInfoCache, userTid, salaryCapType } = useLocal([
		"teamInfoCache",
		"userTid",
		"salaryCapType",
	]);

	const team = teamInfoCache[userTid];
	const canGoOverCap = salaryCapType === "soft" || salaryCapType === "none";

	const reSign = async (p: any) => {
		const errorMsg = await toWorker("main", "acceptContractNegotiation", {
			pid: p.pid,
			amount: p.mood.user.contractAmount,
			exp: p.contract.exp,
		});
		if (errorMsg) {
			logEvent({ type: "error", text: errorMsg, saveToDb: false });
		} else {
			logEvent({
				type: "success",
				text: `Re-signed ${p.firstName} ${p.lastName}.`,
				saveToDb: false,
			});
		}
	};

	const release = async (p: any) => {
		await toWorker("main", "cancelContractNegotiation", p.pid);
	};

	const reSignAll = async () => {
		const proceed = await confirm(
			`Re-sign all ${players.length} ${helpers.plural("player", players.length)} at their asking price?`,
			{ okText: "Re-sign all" },
		);
		if (!proceed) return;
		const errorMsg = await toWorker("main", "reSignAll", players as any);
		if (errorMsg) {
			logEvent({ type: "error", text: errorMsg, saveToDb: false });
		}
	};

	return (
		<div className="gmrs">
			<style>{styles}</style>

			<header className="gmrs-header">
				<div className="gmrs-crest" style={{ background: "#22405a" }}>
					{team?.abbrev ?? "—"}
				</div>
				<div className="gmrs-team">
					<div className="gmrs-team-name">
						{team ? `${team.region} ${team.name}` : "Re-sign Players"}
					</div>
					<div className="gmrs-team-sub">Preseason · re-sign your own</div>
				</div>
			</header>

			<h1 className="gmrs-title">Re-sign Players</h1>

			<section className="gmrs-summary">
				<div className="gmrs-sum-cell">
					<div className="gmrs-sum-val">${capSpace.toFixed(1)}M</div>
					<div className="gmrs-sum-lab">CAP SPACE</div>
				</div>
				<div className="gmrs-sum-cell">
					<div className="gmrs-sum-val">${payroll.toFixed(1)}M</div>
					<div className="gmrs-sum-lab">PAYROLL</div>
				</div>
				<div className="gmrs-sum-cell">
					<div className="gmrs-sum-val">{numRosterSpots}</div>
					<div className="gmrs-sum-lab">ROSTER SPOTS</div>
				</div>
				<div className="gmrs-sum-cell">
					<div className="gmrs-sum-val gmrs-ask">${sumContracts.toFixed(1)}M</div>
					<div className="gmrs-sum-lab">TOTAL ASKING</div>
				</div>
			</section>

			{canGoOverCap ? (
				<p className="gmrs-note">
					You can exceed the cap to re-sign your own players. Anyone you don't
					re-sign now hits free agency — where you can't go over the cap to keep
					them.
				</p>
			) : null}

			{players.length > 0 ? (
				<button className="gmrs-signall" onClick={reSignAll}>
					Re-sign all {players.length}
				</button>
			) : null}

			<div className="gmrs-listhead">
				<span>Expiring contracts</span>
				<span>{players.length} to decide</span>
			</div>

			<div className="gmrs-list">
				{(players as any[]).map((p) => {
					const posColor = POS_COLORS[p.ratings.pos] ?? "#55606a";
					const asking = p.mood.user.contractAmount / 1000;
					// lastSalary is undefined for players with no salary history (e.g. a
					// just-drafted rookie on the re-sign screen); treat that as $0 so the
					// "$old → $new" row renders instead of crashing on undefined.toFixed().
					const oldSalary = p.lastSalary ?? 0;
					const raise = oldSalary > 0 && asking > oldSalary;
					const willing = p.mood.user.willing;
					return (
						<div
							className={`gmrs-card ${p.contract.rookie ? "rookie" : ""}`}
							key={p.pid}
						>
							<div className="gmrs-avatar" style={{ background: posColor }}>
								{(p.firstName?.[0] ?? "") + (p.lastName?.[0] ?? "")}
								<span className="gmrs-ovr">{p.ratings.ovr}</span>
							</div>
							<div className="gmrs-cardbody">
								<div className="gmrs-cardtop">
									<div className="gmrs-name">
										{p.firstName} {p.lastName}
										{p.contract.rookie ? (
											<span className="gmrs-tag">ROOKIE</span>
										) : null}
									</div>
								</div>
								<div className="gmrs-meta">
									{p.ratings.pos} · Age {p.age} · exp {p.contract.exp}
								</div>
								<div className="gmrs-stats">
									{STAT_COLS.map(([label, key]) => (
										<div className="gmrs-stat" key={key}>
											<div className="gmrs-stat-val">
												{helpers.roundStat(p.stats[key], key)}
											</div>
											<div className="gmrs-stat-lab">{label}</div>
										</div>
									))}
									<div className="gmrs-money">
										<span className="gmrs-old">${oldSalary.toFixed(1)}M</span>
										<span className="gmrs-arrow">→</span>
										<span className={`gmrs-new ${raise ? "up" : ""}`}>
											${asking.toFixed(1)}M
										</span>
									</div>
								</div>
								<div className="gmrs-actions">
									{willing ? (
										<button
											className="gmrs-btn gmrs-sign"
											onClick={() => reSign(p)}
										>
											Re-sign ${asking.toFixed(1)}M
										</button>
									) : (
										<span className="gmrs-refuse">Refuses to re-sign</span>
									)}
									<button
										className="gmrs-btn gmrs-release"
										onClick={() => release(p)}
									>
										Let walk
									</button>
								</div>
							</div>
						</div>
					);
				})}
				{players.length === 0 ? (
					<div className="gmrs-empty">
						No players to re-sign. Advance to continue.
					</div>
				) : null}
			</div>
		</div>
	);
};

const styles = `
.gmrs { max-width:720px; margin:0 auto; margin-top:-1rem; padding:16px 16px 40px;
  font-family:Montserrat, system-ui, sans-serif; color:#fff;
  background:linear-gradient(#182a3a, #121212 220px); background-color:#121212; border-radius:12px; }
.gmrs-header { display:flex; align-items:center; gap:12px; padding-top:6px; }
.gmrs-crest { width:34px; height:34px; border-radius:50%; display:flex; align-items:center;
  justify-content:center; font-weight:800; font-size:11px; color:#fff; letter-spacing:.3px; }
.gmrs-team { flex:1; }
.gmrs-team-name { font-weight:800; font-size:15px; }
.gmrs-team-sub { font-size:11px; color:#8fb3c9; }
.gmrs-title { font-weight:900; font-size:34px; letter-spacing:-1px; margin:14px 0 14px; }

.gmrs-summary { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:14px; }
.gmrs-sum-cell { background:#181818; border:1px solid #232323; border-radius:12px; padding:11px 8px;
  text-align:center; }
.gmrs-sum-val { font-weight:900; font-size:17px; letter-spacing:-.5px; }
.gmrs-sum-val.gmrs-ask { color:#18E0A8; }
.gmrs-sum-lab { font-size:8px; letter-spacing:.5px; color:#7f8b93; font-weight:700; margin-top:3px; }

.gmrs-note { font-size:12px; color:#9fb0bc; line-height:1.45; background:#161d24;
  border:1px solid #22303c; border-radius:12px; padding:11px 13px; margin:0 0 14px; }
.gmrs-signall { width:100%; border:none; border-radius:26px; background:#18E0A8; color:#0a0a0a;
  font-family:inherit; font-weight:800; font-size:15px; padding:13px; cursor:pointer;
  box-shadow:0 10px 26px rgba(24,224,168,.3); margin-bottom:18px; }

.gmrs-listhead { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px; }
.gmrs-listhead span:first-child { font-weight:800; font-size:16px; }
.gmrs-listhead span:last-child { font-size:12px; color:#8a8a8a; }
.gmrs-list { display:flex; flex-direction:column; gap:10px; }
.gmrs-card { display:flex; gap:12px; background:#181818; border:1px solid #232323;
  border-radius:13px; padding:12px; }
.gmrs-card.rookie { border-color:rgba(24,224,168,.4); background:rgba(24,224,168,.06); }
.gmrs-avatar { position:relative; width:46px; height:46px; border-radius:11px; flex:0 0 auto;
  display:flex; align-items:center; justify-content:center; font-weight:800; font-size:15px; color:#0a0a0a; }
.gmrs-ovr { position:absolute; bottom:-5px; right:-5px; background:#121212; color:#18E0A8;
  border:1px solid #2a2a2a; border-radius:8px; font-size:10px; font-weight:800; padding:1px 4px; }
.gmrs-cardbody { flex:1; min-width:0; }
.gmrs-name { font-weight:800; font-size:15px; display:flex; align-items:center; gap:7px; }
.gmrs-tag { font-size:8.5px; font-weight:800; letter-spacing:.4px; color:#18E0A8;
  background:rgba(24,224,168,.14); border-radius:8px; padding:2px 6px; }
.gmrs-meta { font-size:11px; color:#8fb3c9; margin:2px 0 8px; }
.gmrs-stats { display:flex; align-items:center; gap:18px; margin-bottom:11px; }
.gmrs-stat-val { font-weight:800; font-size:15px; }
.gmrs-stat-lab { font-size:8.5px; letter-spacing:.5px; color:#7f8b93; font-weight:700; }
.gmrs-money { margin-left:auto; display:flex; align-items:center; gap:6px; font-weight:800; font-size:13px; }
.gmrs-old { color:#7f8b93; text-decoration:line-through; }
.gmrs-arrow { color:#55606a; }
.gmrs-new { color:#fff; }
.gmrs-new.up { color:#E8B923; }
.gmrs-actions { display:flex; gap:8px; align-items:center; }
.gmrs-btn { border:none; border-radius:20px; font-family:inherit; font-weight:800; font-size:12px;
  padding:9px 14px; cursor:pointer; }
.gmrs-sign { background:#18E0A8; color:#0a0a0a; box-shadow:0 8px 20px rgba(24,224,168,.28); }
.gmrs-release { background:#232323; color:#cdd4da; }
.gmrs-refuse { font-size:12px; font-weight:700; color:#E2504E; }
.gmrs-empty { color:#8a8a8a; text-align:center; padding:30px; }
@media (min-width:640px){ .gmrs-list { display:grid; grid-template-columns:1fr 1fr; } }
`;

export default GmNegotiationList;
